// =====================================================================
// AiCreditCostsPanel — super-admin editor for `ai_job_credit_costs`.
//
// `server_cost`  → debited from the user's credit balance per job.
// `client_estimate` → coarse pre-flight courtesy estimate shown in the
//                     Composer pre-flight modal. Always >= server_cost.
// `label`        → human label used in the editor; not user-visible.
//
// Reads via direct supabase select (RLS allows authenticated read).
// Writes via the super-admin-gated `admin-credit-costs-update` edge fn.
// =====================================================================

import { useEffect, useState } from "react";
import { Loader2, Save, Coins } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type CostRow = {
  id: string;
  job_kind: string;
  server_cost: number;
  client_estimate: number;
  label: string;
  updated_at: string;
  updated_by: string | null;
};

type Draft = Pick<CostRow, "server_cost" | "client_estimate" | "label">;

export function AiCreditCostsPanel() {
  const [rows, setRows] = useState<CostRow[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ai_job_credit_costs")
      .select("id, job_kind, server_cost, client_estimate, label, updated_at, updated_by")
      .order("job_kind", { ascending: true });
    setLoading(false);
    if (error) {
      toast.error(`Failed to load credit costs: ${error.message}`);
      return;
    }
    setRows(data ?? []);
    const next: Record<string, Draft> = {};
    for (const r of data ?? []) {
      next[r.job_kind] = {
        server_cost: r.server_cost,
        client_estimate: r.client_estimate,
        label: r.label,
      };
    }
    setDrafts(next);
  };

  useEffect(() => {
    void load();
  }, []);

  const isDirty = (row: CostRow) => {
    const d = drafts[row.job_kind];
    if (!d) return false;
    return (
      d.server_cost !== row.server_cost ||
      d.client_estimate !== row.client_estimate ||
      d.label !== row.label
    );
  };

  const save = async (row: CostRow) => {
    const d = drafts[row.job_kind];
    if (!d) return;
    if (d.client_estimate < d.server_cost) {
      toast.error("Client estimate must be ≥ server cost");
      return;
    }
    setSaving(row.job_kind);
    const { data, error } = await supabase.functions.invoke("admin-credit-costs-update", {
      body: {
        op: "update",
        job_kind: row.job_kind,
        server_cost: d.server_cost,
        client_estimate: d.client_estimate,
        label: d.label,
      },
    });
    setSaving(null);
    if (error) {
      toast.error(`Save failed: ${error.message}`);
      return;
    }
    if ((data as { error?: string } | null)?.error) {
      toast.error(`Save failed: ${(data as { error: string }).error}`);
      return;
    }
    toast.success(`Updated ${row.job_kind}`);
    await load();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-4 w-4" />
          AI job credit costs
        </CardTitle>
        <CardDescription>
          Server cost is debited from the user&apos;s balance per job. Client estimate is the
          courtesy pre-flight number shown in the Composer dialog. Edits audit to{" "}
          <code>admin_actions</code>. Super-admin only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && rows?.length === 0 && (
          <p className="text-sm text-muted-foreground">No credit-cost rows configured.</p>
        )}
        {!loading &&
          rows?.map((row) => {
            const d = drafts[row.job_kind] ?? row;
            const dirty = isDirty(row);
            return (
              <div
                key={row.id}
                className="grid grid-cols-1 gap-3 rounded-md border p-3 md:grid-cols-[1fr_120px_120px_auto]"
              >
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {row.job_kind} · updated{" "}
                    {new Date(row.updated_at).toLocaleDateString()}
                  </Label>
                  <Input
                    value={d.label}
                    onChange={(e) =>
                      setDrafts((s) => ({
                        ...s,
                        [row.job_kind]: { ...d, label: e.target.value },
                      }))
                    }
                    placeholder="Label"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Server cost</Label>
                  <Input
                    type="number"
                    min={1}
                    value={d.server_cost}
                    onChange={(e) =>
                      setDrafts((s) => ({
                        ...s,
                        [row.job_kind]: {
                          ...d,
                          server_cost: Number(e.target.value) || 0,
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Client estimate</Label>
                  <Input
                    type="number"
                    min={1}
                    value={d.client_estimate}
                    onChange={(e) =>
                      setDrafts((s) => ({
                        ...s,
                        [row.job_kind]: {
                          ...d,
                          client_estimate: Number(e.target.value) || 0,
                        },
                      }))
                    }
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    size="sm"
                    onClick={() => save(row)}
                    disabled={!dirty || saving === row.job_kind}
                  >
                    {saving === row.job_kind ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="mr-1 h-4 w-4" /> Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
      </CardContent>
    </Card>
  );
}
